import React, { useState, useEffect } from 'react';
import { productsService, authService } from '../services/api';
import { useNavigate } from 'react-router-dom';

function Products() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderQuantity, setOrderQuantity] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [duplicateReason, setDuplicateReason] = useState('');
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  
  const user = authService.getCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [products, searchTerm, selectedCategory]);

  const getAuthToken = () => {
    const token = localStorage.getItem('ward_stock_token');
    if (!token) {
      alert('Session expired. Please login again.');
      authService.logout();
      navigate('/login');
      return null;
    }
    return token;
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await productsService.getAll();
      if (response.success) {
        setProducts(response.data);
        setFilteredProducts(response.data);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(
          response.data
            .map(p => p.category)
            .filter(cat => cat && cat.trim() !== '')
        )].sort();
        
        setCategories(uniqueCategories);
      }
    } catch (err) {
      setError('Failed to load products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(product =>
        product.product_name.toLowerCase().includes(search) ||
        product.product_code.toLowerCase().includes(search) ||
        (product.subtitle && product.subtitle.toLowerCase().includes(search)) ||
        (product.description && product.description.toLowerCase().includes(search))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    setFilteredProducts(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return null;
    }
    if (imagePath.startsWith('uploads/')) {
      return `https://b1.envicblue.co.uk/${imagePath}`;
    }
    return imagePath;
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    
    if (!orderQuantity || orderQuantity <= 0) {
      alert('Please enter a valid quantity');
      return;
    }

    const token = getAuthToken();
    if (!token) return;

    try {
      const checkResponse = await fetch(`https://b1.envicblue.co.uk/api/orders.php?action=check-duplicate&product_id=${selectedProduct.product_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (checkResponse.status === 401) {
        alert('Session expired. Please login again.');
        authService.logout();
        navigate('/login');
        return;
      }
      
      const checkData = await checkResponse.json();
      
      if (checkData.success && checkData.data && checkData.data.is_duplicate) {
        setDuplicateInfo(checkData.data);
        setShowDuplicateWarning(true);
        return;
      }
    } catch (err) {
      console.error('Duplicate check failed:', err);
    }

    placeOrder();
  };

  const placeOrder = async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch('https://b1.envicblue.co.uk/api/orders.php?action=create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: selectedProduct.product_id,
          quantity: orderQuantity,
          notes: orderNotes,
          duplicate_reason: duplicateReason || null
        })
      });
      
      if (response.status === 401) {
        alert('Session expired. Please login again.');
        authService.logout();
        navigate('/login');
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        alert('✅ Order placed successfully!');
        setShowOrderModal(false);
        setShowDuplicateWarning(false);
        setSelectedProduct(null);
        setOrderQuantity('');
        setOrderNotes('');
        setDuplicateReason('');
      } else {
        alert('❌ Failed to place order: ' + data.message);
      }
    } catch (err) {
      console.error('Order error:', err);
      alert('❌ Network error. Please try again.');
    }
  };

  const getStockStatus = (product) => {
    if (product.current_stock < product.min_stock_level) return 'low';
    if (product.current_stock > product.max_stock_level * 0.8) return 'high';
    return 'normal';
  };

  const getStockColor = (status) => {
    switch (status) {
      case 'low': return '#ef4444';
      case 'high': return '#10b981';
      default: return '#667eea';
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px' }}>⏳</div>
          <div style={{ color: '#666', marginTop: '10px' }}>Loading products...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: window.innerWidth < 768 ? '10px' : '20px', 
      maxWidth: '1400px', 
      margin: '0 auto' 
    }}>
      <div style={{ marginBottom: window.innerWidth < 768 ? '20px' : '30px' }}>
        <h1 style={{ 
          fontSize: window.innerWidth < 768 ? '22px' : '28px', 
          fontWeight: 'bold', 
          color: '#333', 
          marginBottom: '5px' 
        }}>
          📦 Product Catalog
        </h1>
        <p style={{ 
          color: '#666',
          fontSize: window.innerWidth < 768 ? '13px' : '14px'
        }}>Browse and order medical supplies</p>
      </div>

      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          color: '#c33',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Search and Filter Section */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: window.innerWidth < 768 ? '15px' : '24px',
        marginBottom: '30px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
            🔍 Search & Filter
          </h3>
          {(searchTerm || selectedCategory !== 'all') && (
            <button
              onClick={clearFilters}
              style={{
                padding: '8px 16px',
                background: '#f3f4f6',
                color: '#4b5563',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              🔄 Clear
            </button>
          )}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '2fr 1fr',
          gap: '15px'
        }}>
          {/* Search Input */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontSize: '13px', 
              fontWeight: '600', 
              color: '#374151' 
            }}>
              🔎 Search Products
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, code, or description..."
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '14px',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          {/* Category Filter */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontSize: '13px', 
              fontWeight: '600', 
              color: '#374151' 
            }}>
              📂 Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '14px',
                boxSizing: 'border-box',
                cursor: 'pointer',
                background: 'white'
              }}
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Results Count */}
        <div style={{
          marginTop: '15px',
          paddingTop: '15px',
          borderTop: '1px solid #f3f4f6'
        }}>
          <span style={{ 
            fontSize: '14px', 
            color: '#6b7280',
            fontWeight: '600' 
          }}>
            📊 Showing {filteredProducts.length} of {products.length} products
          </span>
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '60px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>📦</div>
          <h3 style={{ fontSize: '20px', color: '#333', marginBottom: '10px' }}>
            No Products Found
          </h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Try adjusting your search or filters
          </p>
          <button
            onClick={clearFilters}
            style={{
              padding: '12px 24px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            🔄 Clear Filters
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth < 768 
            ? '1fr' 
            : 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: window.innerWidth < 768 ? '15px' : '20px'
        }}>
          {filteredProducts.map(product => {
            const stockStatus = getStockStatus(product);
            const stockPercentage = (product.current_stock / product.max_stock_level) * 100;
            const imageUrl = getImageUrl(product.image_path);

            return (
              <div key={product.product_id} style={{
                background: 'white',
                borderRadius: '16px',
                padding: window.innerWidth < 768 ? '15px' : '20px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.08)';
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                  <div style={{ 
                    width: '60px', 
                    height: '60px', 
                    borderRadius: '12px',
                    overflow: 'hidden',
                    background: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={product.product_name}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover' 
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div style="font-size: 28px">📦</div>';
                        }}
                      />
                    ) : (
                      <div style={{ fontSize: '28px' }}>📦</div>
                    )}
                  </div>
                  <div style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    background: `${getStockColor(stockStatus)}15`,
                    color: getStockColor(stockStatus),
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {product.current_stock} units
                  </div>
                </div>

                {/* Product Code */}
                <div style={{
                  fontSize: '12px',
                  color: '#9ca3af',
                  marginBottom: '6px',
                  fontWeight: '500'
                }}>
                  {product.product_code}
                </div>

                <h3 style={{ 
                  fontSize: window.innerWidth < 768 ? '16px' : '18px', 
                  fontWeight: '600', 
                  marginBottom: '8px',
                  color: '#111'
                }}>
                  {product.product_name}
                </h3>

                {product.subtitle && (
                  <p style={{ 
                    fontSize: '13px', 
                    color: '#666', 
                    marginBottom: '12px',
                    lineHeight: '1.4'
                  }}>
                    {product.subtitle}
                  </p>
                )}

                {product.category && (
                  <div style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    background: '#f3f4f6',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#667eea',
                    marginBottom: '12px'
                  }}>
                    📂 {product.category}
                  </div>
                )}

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#999', 
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Stock Level
                  </div>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    background: '#f0f0f0',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${Math.min(stockPercentage, 100)}%`,
                      height: '100%',
                      background: getStockColor(stockStatus),
                      transition: 'width 0.3s'
                    }}></div>
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#999',
                    marginTop: '4px',
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}>
                    <span>Min: {product.min_stock_level}</span>
                    <span>Max: {product.max_stock_level}</span>
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid #f0f0f0'
                }}>
                  <div>
                    <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Price</div>
                    <div style={{ 
                      fontSize: window.innerWidth < 768 ? '20px' : '24px', 
                      fontWeight: 'bold', 
                      color: '#10b981' 
                    }}>
                      £{parseFloat(product.price).toFixed(2)}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedProduct(product);
                      setOrderQuantity('');
                      setOrderNotes('');
                      setShowOrderModal(true);
                    }}
                    style={{
                      padding: window.innerWidth < 768 ? '10px 16px' : '12px 24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: window.innerWidth < 768 ? '13px' : '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    🛒 Order
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Order Modal */}
      {showOrderModal && selectedProduct && !showDuplicateWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={() => setShowOrderModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: window.innerWidth < 768 ? '20px' : '30px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}
          onClick={(e) => e.stopPropagation()}>
            <h2 style={{ 
              fontSize: window.innerWidth < 768 ? '20px' : '24px', 
              fontWeight: 'bold', 
              marginBottom: '20px',
              color: '#1f2937'
            }}>
              🛒 Place Order
            </h2>

            <div style={{
              background: '#f9fafb',
              padding: '15px',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '5px' }}>Product</p>
              <p style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                {selectedProduct.product_name}
              </p>
              <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>
                Code: {selectedProduct.product_code} • £{parseFloat(selectedProduct.price).toFixed(2)}
              </p>
            </div>

            <form onSubmit={handlePlaceOrder}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151'
                }}>
                  Quantity *
                </label>
                <input
                  type="number"
                  min="1"
                  required
                  value={orderQuantity}
                  onChange={(e) => setOrderQuantity(e.target.value)}
                  placeholder="Enter quantity"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  fontSize: '14px',
                  color: '#374151'
                }}>
                  Notes (Optional)
                </label>
                <textarea
                  rows="3"
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="Add any notes about this order..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowOrderModal(false);
                    setSelectedProduct(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: '#f3f4f6',
                    color: '#4b5563',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Place Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Duplicate Warning Modal */}
      {showDuplicateWarning && duplicateInfo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: window.innerWidth < 768 ? '20px' : '30px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ 
              fontSize: window.innerWidth < 768 ? '20px' : '24px', 
              fontWeight: 'bold', 
              marginBottom: '15px',
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              ⚠️ Duplicate Order Warning
            </h2>

            <div style={{
              background: '#fef3c7',
              border: '2px solid #fbbf24',
              padding: '15px',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <p style={{ fontSize: '14px', color: '#92400e', marginBottom: '10px' }}>
                You've already ordered this product this week:
              </p>
              <p style={{ fontSize: '14px', color: '#78350f', fontWeight: '600' }}>
                {duplicateInfo.product_name}
              </p>
              <p style={{ fontSize: '13px', color: '#92400e', marginTop: '5px' }}>
                Previous order: {duplicateInfo.quantity} units on {new Date(duplicateInfo.created_at).toLocaleDateString()}
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: '600',
                fontSize: '14px',
                color: '#374151'
              }}>
                Please provide a reason for ordering again:
              </label>
              <textarea
                rows="3"
                value={duplicateReason}
                onChange={(e) => setDuplicateReason(e.target.value)}
                placeholder="e.g., Emergency stock needed, Increased patient load..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowDuplicateWarning(false);
                  setShowOrderModal(false);
                  setDuplicateReason('');
                }}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: '#f3f4f6',
                  color: '#4b5563',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={placeOrder}
                disabled={!duplicateReason.trim()}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: duplicateReason.trim() ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: duplicateReason.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;
